import type { AppsUpsertPlatformCredentialRequest, AppsUpsertPlatformRequest } from '@customerio/hydra-ui-api';
import type { PushActionType } from '../types';

/**
 * The body of `PUT apps/{id}/platforms/{platform}/credential` — a new credential
 * uploaded for one platform of an app. `action_type` is narrowed to the two push
 * slots these forms can write.
 */
export type AppCredentialPayload = Omit<AppsUpsertPlatformCredentialRequest, 'action_type'> & {
  action_type: PushActionType;
};

/**
 * The body of `PUT apps/{id}/platforms` when a platform reuses a credential that
 * already exists, identified by id. The platform itself comes from the route the
 * caller writes to, so only the identifier and the credential id are decided here.
 */
export type AppCredentialLink = Omit<AppsUpsertPlatformRequest, 'platform' | 'credential_id'> & {
  credential_id: number;
};

/** What a platform's credential field can produce: a new upload, or a reuse link. */
export type AppCredentialValue = AppCredentialPayload | AppCredentialLink;

/** Whether a platform's credential reuses an existing one rather than uploading a new one. */
export function isCredentialLink(value: AppCredentialValue) {
  return 'credential_id' in value;
}

/** True when every value is a non-blank string. */
export function allPresent(...values: string[]): boolean {
  return values.every((value) => value.trim() !== '');
}

export type ApnsCredentialInput = {
  keyID: string;
  teamID: string;
  bundleID: string;
  fileName: string;
  privateKey: string;
  sandbox: boolean;
};

/**
 * APNS: the identifier is the Bundle ID, the credential password is the JSON blob
 * the backend expects, and `config` carries the sandbox flag.
 */
export function buildApnsCredential(input: ApnsCredentialInput): AppCredentialPayload {
  const password = JSON.stringify({
    key_id: input.keyID.trim(),
    team_id: input.teamID.trim(),
    bundle_id: input.bundleID.trim(),
    private_key: input.privateKey,
  });

  return {
    identifier: input.bundleID.trim(),
    action_type: 'push_apns',
    credential: {
      username: input.fileName,
      password,
      config: input.sandbox ? 'sandbox' : '',
    },
  };
}

export type FcmCredentialInput = {
  /**
   * Platform-specific: iOS identifies itself by its Bundle ID, Android by its
   * app identifier.
   */
  identifier: string;
  fileName: string;
  rawJson: string;
};

/**
 * FCM: the raw service-account JSON is the credential password, and
 * `config: 'allow_ios'` lets iOS send through it too.
 */
export function buildFcmCredential(input: FcmCredentialInput): AppCredentialPayload {
  return {
    identifier: input.identifier,
    action_type: 'push_fcm',
    credential: {
      username: input.fileName,
      password: input.rawJson,
      config: 'allow_ios',
    },
  };
}

/**
 * The FCM `project_id` from a service-account JSON file's contents, or null when
 * the file is not valid JSON or carries no string `project_id`.
 */
export function parseFcmProjectId(rawJson: string): string | null {
  try {
    const parsed: unknown = JSON.parse(rawJson);
    if (parsed == null || typeof parsed !== 'object' || !('project_id' in parsed)) {
      return null;
    }
    const { project_id: projectId } = parsed;
    return typeof projectId === 'string' ? projectId : null;
  } catch {
    return null;
  }
}
